<template>
<div class="container-fluid p-0">
    <div class="row">
        <CCol sm="12">
            <CCard>
                <CCardBody>
                    <div class="d-flex mb-4 justify-content-between align-items-center">
                        <!-- Title -->
                        <span v-if="name"><b>{{ $t('wineIntro') }}</b> {{name}}</span>
                        <!-- Language dropdown -->
                        <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                    </div>
                    
                    <div class="wine-form-container">
                        <!-- Left side - Wine Image -->
                        <div class="wine-image-section">
                            <div role="group" class="form-group">
                                <GalleryPicker 
                                    class="gallery-picker" 
                                    v-model="wine.photo" 
                                    :ratioWidth="2" 
                                    :ratioHeight="7" 
                                    v-bind:class="{'is-invalid': genericValidator(wine.photo) === false || errorValidator('Photo') === false }" 
                                    :max="1">
                                </GalleryPicker>
                            </div>
                        </div>

                        <!-- Right side - Form Fields -->
                        <div class="wine-form-section">
                            <div class="row">
                                <!-- Position -->
                                <div class="col-12 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('positionOptional') }}</label>
                                        <CInput 
                                            type="number" 
                                            v-model="wine.position" 
                                            :invalid-feedback="errorOutput['Position']"
                                            :is-valid="errorValidator('Position')">
                                        </CInput>
                                    </div>
                                </div>

                                <!-- Names -->
                                <div class="col-12 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('nameMandatory') }}</label>
                                        <VueEditor 
                                            v-model="wine.names[currentLanguage.key]" 
                                            v-bind:class="{'is-invalid': genericValidator(wine.names) == false || errorValidator('Names') }">
                                        </VueEditor>
                                        <div v-if="errorOutput['Names']" class="invalid-feedback">{{errorOutput['Names']}}</div>
                                    </div>
                                </div>

                                <!-- Consumptions -->
                                <div class="col-12 col-lg-6 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('consumptionMandatory') }}</label>
                                        <VueEditor 
                                            v-model="wine.consumptions[currentLanguage.key]" 
                                            v-bind:class="{'is-invalid': genericValidator(wine.consumptions) == false || errorValidator('Consumptions') }">
                                        </VueEditor>
                                        <div v-if="errorOutput['Consumptions']" class="invalid-feedback">{{errorOutput['Consumptions']}}</div>
                                    </div>
                                </div>

                                <!-- Temperatures -->
                                <div class="col-12 col-lg-6 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('temperatureMandatory') }}</label>
                                        <CInput 
                                            type="text" 
                                            v-model="wine.temperatures[currentLanguage.key]" 
                                            :invalid-feedback="errorOutput['Temperatures']" 
                                            :is-valid="genericValidator(wine.temperatures) && errorValidator('Temperatures')">
                                        </CInput>
                                    </div>
                                </div>

                                <!-- Castes -->
                                <div class="col-12 col-lg-6 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('casteMandatory') }}</label>
                                        <CInput 
                                            type="text" 
                                            v-model="wine.castes[currentLanguage.key]" 
                                            :invalid-feedback="errorOutput['Castes']" 
                                            :is-valid="genericValidator(wine.castes) && errorValidator('Castes')">
                                        </CInput>
                                    </div>
                                </div>

                                <!-- Stages -->
                                <div class="col-12 col-lg-6 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('stageMandatory') }}</label>
                                        <CInput 
                                            type="text" 
                                            v-model="wine.stages[currentLanguage.key]" 
                                            :invalid-feedback="errorOutput['Stages']" 
                                            :is-valid="genericValidator(wine.stages) && errorValidator('Stages')">
                                        </CInput>
                                    </div>
                                </div>

                                <!-- Datasheet -->
                                <div class="col-12 col-lg-6 mb-4">
                                    <CustomPicker 
                                        v-for="language in supportedLanguages" 
                                        :key="`datasheet-${language.key}`" 
                                        v-show="language.key == currentLanguage.key" 
                                        :identifier="language.key" 
                                        :title="$t('datasheet')" 
                                        class="form-group" 
                                        v-model="wine.datasheets[language.key]" 
                                        v-bind:class="{'is-invalid': errorValidator('Datasheets')}">
                                    </CustomPicker>
                                </div>

                                <!-- Nutritional sheet -->
                                <div class="col-12 col-lg-6 mb-4">
                                    <CustomPicker 
                                        v-for="language in supportedLanguages" 
                                        :key="`nutritionalSheet-${language.key}`" 
                                        v-show="language.key == currentLanguage.key" 
                                        :identifier="language.key" 
                                        :title="$t('nutritionalSheet')" 
                                        class="form-group" 
                                        v-model="wine.nutritionalSheets[language.key]" 
                                        v-bind:class="{'is-invalid': errorValidator('nutritionalSheets')}">
                                    </CustomPicker>
                                </div>

                                <!-- Prizes -->
                                <div class="col-12 col-lg-6 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('prizes') }}</label>
                                        <OptionsDropdown 
                                            v-model="wine.prizes" 
                                            :options="prizes" 
                                            :title="$t('linkPrize')" 
                                            :index="1" 
                                            :is-valid="errorValidator('Prizes')" 
                                            :invalid-feedback="errorOutput['Prizes']">
                                        </OptionsDropdown>
                                    </div>
                                </div>

                                <!-- Type -->
                                <div class="col-12 col-lg-6 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('typesMandatory') }}</label>
                                        <OptionsDropdown 
                                            v-model="wine.types" 
                                            :options="types" 
                                            :title="$t('type')" 
                                            :index="2" 
                                            :is-valid="genericValidator(wine.types) && errorValidator('Types')" 
                                            :invalid-feedback="errorOutput['Types']">
                                        </OptionsDropdown>
                                    </div>
                                </div>

                                <!-- Year -->
                                <div class="col-12 col-lg-6 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('yearMandatory') }}</label>
                                        <CInput 
                                            type="number" 
                                            v-model="wine.year" 
                                            :invalid-feedback="errorOutput['Year']" 
                                            :is-valid="genericValidator(wine.year) && errorValidator('Year')">
                                        </CInput>
                                    </div>
                                </div>

                                <!-- Description -->
                                <div class="col-12 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('descriptionMandatory') }}</label>
                                        <VueEditor 
                                            v-model="wine.descriptions[currentLanguage.key]" 
                                            v-bind:class="{'is-invalid': genericValidator(wine.descriptions) == false || errorValidator('Descriptions') }">
                                        </VueEditor>
                                        <div v-if="errorOutput['Descriptions']" class="invalid-feedback">{{errorOutput['Descriptions']}}</div>
                                    </div>
                                </div>

                                <!-- Action button + submitting -->  
                                <div class="col-12">
                                    <div class="d-flex justify-content-end">
                                        <CSpinner v-if="submitting" variant="success" small></CSpinner>
                                        <CButton v-else type="button" class="btn btn-primary" @click="submitClicked">
                                            {{ $t('acceptChanges') }}
                                        </CButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Loading overlay -->
                    <FormLoading v-if="loading"></FormLoading>
                </CCardBody>
            </CCard>
        </CCol>
    </div>
    <ConfirmationPopUp 
        :confirmationMessage="confirmationMessage" 
        @closeConfirmRequested="onClickConfirmationPopup" 
        v-model="showConfirmationPopup">
    </ConfirmationPopUp>
</div>
</template>
 
<script>
import Wine from "@/classes/Wine";
import { mapState, mapActions } from 'vuex'
import LanguageDropdown from "@/controls/LanguageDropdown.vue";
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import GalleryPicker from '@/controls/GalleryPicker.vue'
import { VueEditor } from "vue2-editor";
import CustomPicker from '@/controls/CustomPicker.vue'
import OptionsDropdown from '@/controls/OptionsDropdown.vue'
import FormLoading from '@/controls/FormLoading.vue'
import InputGetList from "@/classes/InputGetList";
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import { API_HELPER } from "@/helpers/api.js";

  export default {
  components: { LanguageDropdown, ConfirmationPopUp, GalleryPicker, VueEditor, CustomPicker, OptionsDropdown, FormLoading },
    props:{    
        baseWine:
        {
            type: Object,
            default: null,
        },
        baseWineName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            triedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            wine: new Wine({}, {}, {}, {}, {}, {}, {}, [], [], []),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
            //
            types: [{label: this.$t("white"), key: "White"}, {label: this.$t("red"), key: "Red"}, {label: this.$t("reserve"), key: "Reserve"}, {label: this.$t("rose"), key: "Rosé"}],
        }
    },
    mounted: function()
    {
        var input = new InputGetList(999, 1, null, 1, "id");
        this.getPrizes(input);

        this.resetWine();
    },
    computed:
    {
        supportedLanguages: function()
        {
            return CONSTANTS.supportedLanguages;
        },
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        errorOutput: function()
        {
            var result = [];

            if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
            {
                result = this.alertOutput.reduce(function(accum, currentVal) {
                accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate multilanguage fields
            for (const [key, value] of Object.entries(this.wine.names)) { if(!value) { result['Names'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.wine.descriptions)) { if(!value) { result['Descriptions'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.wine.consumptions)) { if(!value) { result['Consumptions'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.wine.temperatures)) { if(!value) { result['Temperatures'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.wine.castes)) { if(!value) { result['Castes'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.wine.stages)) { if(!value) { result['Stages'] = this.$i18n.t("validateBothTranslations"); break; }; }

            return result;
        },
        ...mapState('wines', ['postWineOutput']),
        ...mapState('wines', ['putWineOutput']),
        ...mapState('prizes', ['getPrizesOutput']),
        prizes: function()
        {
            var result = [];

            if(this.getPrizesOutput && this.getPrizesOutput.data)
            {
                this.getPrizesOutput.data.forEach(prize => 
                {
                    result.push({key: prize.id, label: prize.title });
                });
            }

            return result;
        },
        name: function()
        {
            return this.baseWine || this.baseWineName ? (this.baseWine ? this.baseWine.name : this.baseWineName) : null;
        }
    },
    methods:{
        ...mapActions('prizes', ['getPrizes']),
        resetWine(){
            if(!this.baseWine)
            {
                var names = {}; var descriptions = {}; var consumptions = {}; var temperatures = {}; var castes = {}; var stages = {}; var datasheets = {}; var nutritionalSheets = {};
                CONSTANTS.supportedLanguages.forEach(language => { names[language.key] = ""; descriptions[language.key] = ""; consumptions[language.key] = ""; temperatures[language.key] = ""; castes[language.key] = ""; stages[language.key] = ""; datasheets[language.key] = null; nutritionalSheets[language.key] = null; });

                this.wine = new Wine(names, descriptions, consumptions, temperatures, castes, stages, datasheets, nutritionalSheets, [], []);
            }
            else
            {
                this.wine = deepClone(new Wine, this.baseWine);    
                this.wine.photo = {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.wine.photoPath])};
                this.wine.prizes = this.wine.prizes.map(prize => prize.id);    
            }
        },
        submitClicked(){
            // console.log(this.wine);
            this.triedSubmit = true;
            // Emit event
            if(this.wine.isValid())
            {
                this.submitting = true;
                this.$emit('submit-requested', this.wine);
            }
        },
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            this.$router.push({name: "WinesList" });
        },
        genericValidator,
        errorValidator
    },
    watch:
    {
        postWineOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        putWineOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        baseWine: function()
        {
            this.resetWine();
        },
        alertType: function()
        {
            this.submitting = false;
        },
        alertOutput: function()
        {
            this.submitting = false;

            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>
    .showDropdown {
        display: block !important;
    }

    .input-group-append {
        height: fit-content;
    }

    .wine-form-container {
        display: flex;
        flex-wrap: wrap;
        gap: 2rem;
    }

    .wine-image-section {
        flex: 0 0 300px;
    }

    .wine-form-section {
        flex: 1;
        min-width: 300px;
    }

    .gallery-picker {
        position: sticky;
        top: 1rem;
    }

    .gallery-picker :deep(img) {
        width: 100%;
        height: auto;
        max-height: 800px;
        object-fit: contain;
    }

    /* Container for the image to maintain aspect ratio */
    .gallery-picker :deep(.image-container) {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #f8f9fa;
        border-radius: 4px;
        padding: 1rem;
    }

    @media (max-width: 991.98px) {
        .wine-image-section {
            flex: 0 0 100%;
            max-width: 400px;
            margin: 0 auto;
        }

        .gallery-picker {
            position: static;
            margin-bottom: 2rem;
        }

        .gallery-picker :deep(img) {
            max-height: 350px;
        }
    }

    @media (max-width: 575.98px) {
        .wine-image-section {
            max-width: 280px;
        }

        .gallery-picker :deep(img) {
            max-height: 280px;
        }
    }
</style> 